<div class="pb-page">

  <app-prevention-group-header activeTab="needs">
    <div class="header-controls">
      <div class="period-selector">
        @for (d of [7, 30, 90]; track d) {
          <button class="period-btn" [class.active]="periodDays() === d" (click)="setPeriodDays(d)">
            {{ 'pilotagePrevention.period.' + d | translate }}
          </button>
        }
      </div>
      <select
        class="select-ctrl"
        [attr.aria-label]="'opportunites.header.ageFilter.label' | translate"
        [value]="ageBand() ?? ''"
        (change)="setAgeBand($any($event.target).value)">
        <option value="">{{ 'opportunites.header.ageFilter.all' | translate }}</option>
        @for (band of ageBands; track band) {
          <option [value]="band">{{ band }}</option>
        }
      </select>
    </div>
  </app-prevention-group-header>

  <p class="page-subtitle">{{ 'opportunites.header.subtitle' | translate }}</p>

  @if (loadError()) {
    <div class="cs" style="text-align:center;padding:40px 0">{{ 'opportunites.states.error' | translate }}</div>
  } @else if (!dashboard()) {
    <div class="cs" style="text-align:center;padding:40px 0">{{ 'opportunites.states.loading' | translate }}</div>
  } @else if (dashboard(); as d) {

    <!-- Disponibilité des données : ligne discrète. Le contrat unifié ne porte pas de bloc "qualité"
         (complétude Coverage/âge/contexte) : seul le seuil d'affichage est expliqué ici. -->
    <div class="quality-bar">
      <span class="quality-since">
        @if (d.dataAvailableFrom) {
          {{ 'opportunites.quality.availableSince' | translate: { date: formatDate(d.dataAvailableFrom) } }}
        } @else {
          {{ 'opportunites.quality.availableSinceUnknown' | translate }}
        }
      </span>
      <span class="quality-sep">·</span>
      <button
        type="button"
        class="quality-info-btn"
        [attr.aria-expanded]="qualityPanelOpen()"
        (click)="toggleQualityPanel(); $event.stopPropagation()">
        {{ 'opportunites.quality.label' | translate }} <span aria-hidden="true">ⓘ</span>
      </button>

      @if (qualityPanelOpen()) {
        <div class="quality-panel" (click)="$event.stopPropagation()">
          @if (noDataAtAll()) {
            <div class="quality-row-value" style="text-align:left">{{ 'opportunites.quality.panel.noDataTitle' | translate }}</div>
            <div class="cs" style="margin-top:6px">{{ 'opportunites.quality.panel.noDataSubtitle' | translate }}</div>
          } @else {
            <div class="quality-row-value" style="text-align:left">{{ 'opportunites.quality.panel.thresholdTitle' | translate }}</div>
            <div class="cs" style="margin-top:6px">{{ 'opportunites.quality.panel.thresholdExplanation' | translate: { threshold: d.smallCohortThreshold } }}</div>
            <div class="cs" style="margin-top:6px">{{ 'opportunites.quality.panel.thresholdScope' | translate }}</div>
          }
        </div>
      }
    </div>

    <!-- Absence TOTALE de données (aucune session terminée) -->
    @if (noDataAtAll()) {
      <div class="empty-state-banner">
        <div class="empty-state-title">{{ 'opportunites.emptyState.noData.title' | translate }}</div>
        <div class="empty-state-subtitle">{{ 'opportunites.emptyState.noData.subtitle' | translate }}</div>
      </div>
    }

    <!-- Des sessions existent mais la cohorte globale reste sous le seuil (drapeau API `globalCohortInsufficient`) -->
    @if (collectingNotYetSufficient()) {
      <div class="empty-state-banner">
        <div class="empty-state-title">{{ 'opportunites.emptyState.collecting.title' | translate }}</div>
        <div class="empty-state-subtitle">{{ 'opportunites.emptyState.collecting.subtitle' | translate }}</div>
        <div class="empty-state-subtitle">{{ 'opportunites.emptyState.collecting.thresholdNote' | translate: { threshold: d.smallCohortThreshold } }}</div>
      </div>
    }

    <!-- 1. KPI globaux : uniquement ce que l'API fournit. Totaux retenus (`totalsWithheld`) ou valeur
         `null` : la carte concernée est MASQUÉE -- jamais 0, jamais « — », jamais une somme des lignes. -->
    <div class="card" style="margin-bottom:20px">
      <div class="kpi-grid" [style.--kpi-cols]="kpiColumnCount()">
        <div class="kpi-card">
          <div class="kpi-label">{{ 'opportunites.kpi.completedSessions' | translate }}</div>
          <div class="kpi-value">{{ d.completedSessions }}</div>
        </div>

        @if (opportunityCount() !== null) {
          <div class="kpi-card">
            <div class="kpi-label">
              {{ 'opportunites.kpi.identified' | translate }}
              <span class="kpi-label-info" [attr.title]="'opportunites.kpi.identifiedTooltip' | translate">ⓘ</span>
            </div>
            <div class="kpi-value">{{ opportunityCount() }}</div>
          </div>
        }

        @if (distinctSessions() !== null) {
          <div class="kpi-card">
            <div class="kpi-label">
              {{ 'opportunites.kpi.sessions' | translate }}
              <span class="kpi-label-info" [attr.title]="'opportunites.kpi.sessionsTooltip' | translate">ⓘ</span>
            </div>
            <div class="kpi-value">{{ distinctSessions() }}</div>
          </div>
        }

        @if (uncoveredCount() !== null) {
          <div class="kpi-card">
            <div class="kpi-label">{{ 'opportunites.kpi.uncovered' | translate }}</div>
            <div class="kpi-value">{{ uncoveredCount() }}</div>
          </div>
        }
      </div>

      @if (showTotalsWithheldNotice()) {
        <div class="table-notice kpi-notice">{{ 'opportunites.totalsWithheldNotice' | translate }}</div>
      }
    </div>

    <!-- 2. À retenir (maximum 1 insight) -->
    @if (keyInsight(); as insight) {
      <div class="card insight-card" style="margin-bottom:20px">
        <div class="ct">{{ 'opportunites.insight.title' | translate }}</div>
        @if (insight.kind === 'topUncovered') {
          <div class="insight-text">{{ 'opportunites.insight.topUncovered' | translate: { subject: insight.subjectLabel } }}</div>
          <div class="cs" style="margin-top:2px">
            {{ (insight.uncoveredCount > 1 ? 'opportunites.insight.topUncoveredDetailPlural' : 'opportunites.insight.topUncoveredDetailSingular') | translate: { count: insight.uncoveredCount, sites: insight.uncoveredSiteCount } }}
          </div>
          <button class="insight-cta" (click)="goToSubject(insight.subjectKey)">{{ 'opportunites.insight.view' | translate }} →</button>
        } @else {
          <div class="insight-text">{{ 'opportunites.insight.goodCoverage' | translate: { rate: formatRate(insight.rate) } }}</div>
        }
      </div>
    }

    <!-- 3. Détail par opportunité : vue de comparaison principale, une ligne par sujet renvoyé par l'API
         (aucune distinction visible entre sujets cliniques et sujets catalogue). -->
    <div class="card card--secondary">
      <div class="ct ct--secondary">{{ 'opportunites.table.title' | translate }}</div>

      @if (sortedOpportunities().length > 0) {
        <div class="need-table">
          <div class="need-row need-row--header">
            <span>{{ 'opportunites.table.columns.subject' | translate }}</span>
            <button class="need-row-sort" (click)="toggleSort('detectedSessions')">{{ 'opportunites.table.columns.identified' | translate }}</button>
            <button class="need-row-sort" (click)="toggleSort('uncoveredCount')">{{ 'opportunites.table.columns.uncovered' | translate }}</button>
            <span>{{ 'opportunites.table.columns.sites' | translate }}</span>
            <span>{{ 'opportunites.table.columns.action' | translate }}</span>
          </div>
          @for (row of sortedOpportunities(); track row.subjectKey) {
            <div
              class="need-row need-row--data"
              role="button"
              tabindex="0"
              [attr.aria-label]="row.subjectLabel"
              (click)="goToSubject(row.subjectKey)"
              (keydown.enter)="goToSubject(row.subjectKey)">
              <span class="need-row-name">{{ row.subjectLabel }}</span>
              @if (row.insufficientData) {
                <span class="need-row-masked">
                  <span class="need-row-masked-title">{{ 'opportunites.states.insufficientData' | translate }}</span>
                  <span class="need-row-masked-sub">{{ 'opportunites.states.rowSubtitle' | translate: { threshold: d.smallCohortThreshold } }}</span>
                </span>
              } @else {
                <span>{{ formatCount(row.detectedSessions) }}</span>
                <span class="uncovered-cell">
                  <span class="uncovered-value">{{ formatCount(row.uncoveredCount) }} {{ 'opportunites.table.uncoveredUnit' | translate }}</span>
                  <span class="uncovered-coverage">{{ formatUncoveredCoverageNote(row.coverageRate) }}</span>
                </span>
                <span>{{ formatCount(row.uncoveredSiteCount) }}</span>
                <span class="action-link">{{ 'opportunites.table.viewAction' | translate }} →</span>
              }
            </div>
          }
        </div>

        <div class="need-cards">
          @for (row of sortedOpportunities(); track row.subjectKey) {
            <div
              class="need-card"
              role="button"
              tabindex="0"
              [attr.aria-label]="row.subjectLabel"
              (click)="goToSubject(row.subjectKey)"
              (keydown.enter)="goToSubject(row.subjectKey)">
              <div class="need-card-title">{{ row.subjectLabel }}</div>
              @if (row.insufficientData) {
                <div class="need-row-masked">
                  <span class="need-row-masked-title">{{ 'opportunites.states.insufficientData' | translate }}</span>
                  <span class="need-row-masked-sub">{{ 'opportunites.states.rowSubtitle' | translate: { threshold: d.smallCohortThreshold } }}</span>
                </div>
              } @else {
                <div class="need-card-grid">
                  <div><span class="cs">{{ 'opportunites.table.columns.identified' | translate }}</span><br>{{ formatCount(row.detectedSessions) }}</div>
                  <div>
                    <span class="cs">{{ 'opportunites.table.columns.uncovered' | translate }}</span><br>
                    <span class="uncovered-value">{{ formatCount(row.uncoveredCount) }} {{ 'opportunites.table.uncoveredUnit' | translate }}</span>
                    <span class="uncovered-coverage">{{ formatUncoveredCoverageNote(row.coverageRate) }}</span>
                  </div>
                  <div><span class="cs">{{ 'opportunites.table.columns.sites' | translate }}</span><br>{{ formatCount(row.uncoveredSiteCount) }}</div>
                </div>
              }
            </div>
          }
        </div>
      } @else {
        <div class="cs" style="text-align:center;padding:20px 0">{{ 'opportunites.emptyState.list' | translate }}</div>
      }
    </div>
  }
</div>
